/**
 * Central logging utility for RadixDash application
 * Provides structured logging with redaction, context, and pluggable backend support
 * 
 * @module logger
 */

// Sensitive field patterns to redact
const SENSITIVE_PATTERNS = [
  'password',
  'token',
  'authorization',
  'auth',
  'secret',
  'apikey',
  'api_key',
  'access_token',
  'refresh_token',
  'private_key',
  'ssn',
  'social_security',
  'credit_card',
  'card_number',
  'cvv',
  'pin'
];

// PII field patterns to redact in production
const PII_PATTERNS = [
  'email',
  'phone',
  'phoneNumber',
  'mobile',
  'address',
  'street',
  'zipcode',
  'postal_code',
  'date_of_birth',
  'dob',
  'birthdate',
  'salary',
  'wage',
  'income'
];

// Log levels
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

// Global context that gets merged into all logs
let globalContext = {};

// Current log level (configurable via env or localStorage)
let currentLogLevel = process.env.NODE_ENV === 'production' ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG;

// Redaction settings
let redactionEnabled = process.env.NODE_ENV === 'production';

// Check localStorage for dev overrides (only in browser environment)
if (typeof window !== 'undefined' && window.localStorage) {
  // Allow dev mode log level override
  const storedLevel = localStorage.getItem('LOG_LEVEL');
  if (storedLevel && LOG_LEVELS[storedLevel] !== undefined) {
    currentLogLevel = LOG_LEVELS[storedLevel];
  }
  
  // Allow dev mode redaction override (NEVER in production)
  if (process.env.NODE_ENV !== 'production') {
    const disableRedaction = localStorage.getItem('DISABLE_REDACTION');
    if (disableRedaction === 'true') {
      redactionEnabled = false;
    }
  }
}

/**
 * Deeply redacts sensitive fields from an object
 * @param {*} obj - Object to redact (can be any type)
 * @param {Array<string>} patterns - Field patterns to redact
 * @returns {*} Redacted copy of the object
 * @private
 */
function redactObject(obj, patterns = []) {
  // Handle null/undefined
  if (obj == null) return obj;
  
  // Handle primitives
  if (typeof obj !== 'object') return obj;
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => redactObject(item, patterns));
  }
  
  // Handle objects
  const redacted = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    
    // Check if this key matches any sensitive pattern
    const isSensitive = patterns.some(pattern => 
      lowerKey.includes(pattern.toLowerCase())
    );
    
    if (isSensitive) {
      redacted[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      // Recursively redact nested objects
      redacted[key] = redactObject(value, patterns);
    } else {
      redacted[key] = value;
    }
  }
  
  return redacted;
}

/**
 * Safely stringifies objects, handling circular references
 * @param {*} obj - Object to stringify
 * @returns {string} JSON string or error message
 * @private
 */
function safeStringify(obj) {
  const seen = new WeakSet();
  try {
    return JSON.stringify(obj, (key, value) => {
      // Handle circular references
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      
      // Handle BigInt
      if (typeof value === 'bigint') {
        return value.toString();
      }
      
      // Handle functions
      if (typeof value === 'function') {
        return '[Function]';
      }
      
      return value;
    });
  } catch (error) {
    return `[Stringify Error: ${error.message}]`;
  }
}

/**
 * Summarizes large data structures for logging
 * @param {*} data - Data to potentially summarize
 * @param {WeakSet} [seen] - Track visited objects to prevent circular references
 * @returns {*} Summarized or original data
 * @private
 */
function summarizeData(data, seen = new WeakSet()) {
  // Don't summarize primitives
  if (!data || typeof data !== 'object') return data;
  
  // Handle circular references
  if (seen.has(data)) {
    return '[Circular Reference]';
  }
  seen.add(data);
  
  // Handle arrays
  if (Array.isArray(data)) {
    if (data.length > 100) {
      return {
        _summary: 'Large Array',
        length: data.length,
        sample: data.slice(0, 3).map(item => summarizeData(item, seen)),
        type: data[0] ? typeof data[0] : 'unknown'
      };
    }
    // Process array elements recursively
    return data.map(item => summarizeData(item, seen));
  }
  
  // Handle objects
  const keys = Object.keys(data);
  if (keys.length > 50) {
    const sampleResult = {};
    keys.slice(0, 5).forEach(key => {
      sampleResult[key] = summarizeData(data[key], seen);
    });
    return {
      _summary: 'Large Object',
      keyCount: keys.length,
      sampleKeys: keys.slice(0, 5),
      sample: sampleResult,
      _truncated: true
    };
  }
  
  // Process object properties recursively
  const result = {};
  for (const [key, value] of Object.entries(data)) {
    result[key] = summarizeData(value, seen);
  }
  return result;
}

/**
 * Core logging function that handles all log output
 * @param {string} level - Log level (DEBUG, INFO, WARN, ERROR)
 * @param {string} msg - Human-readable log message
 * @param {Object} meta - Structured metadata object
 * @private
 */
function log(level, msg, meta = {}) {
  // Check if this level should be logged
  if (LOG_LEVELS[level] < currentLogLevel) return;
  
  // First summarize large data structures
  let processedMeta = summarizeData(meta);
  
  // Then apply redaction to metadata
  if (redactionEnabled) {
    // In production or when redaction is enabled, always redact PII
    const patterns = [...SENSITIVE_PATTERNS, ...PII_PATTERNS];
    processedMeta = redactObject(processedMeta, patterns);
  }
  
  // Build log entry
  const logEntry = {
    level,
    msg,
    meta: processedMeta,
    ts: new Date().toISOString(),
    ...globalContext
  };
  
  // Output to appropriate console method
  try {
    const logString = safeStringify(logEntry);
    
    switch (level) {
      case 'ERROR':
        console.error(logString);
        break;
      case 'WARN':
        console.warn(logString);
        break;
      case 'DEBUG':
        if (process.env.NODE_ENV !== 'production') {
          console.log(logString);
        }
        break;
      default:
        console.log(logString);
    }
  } catch (error) {
    // Fail-safe: logger should never throw
    console.error('[Logger Error]', error.message);
  }
}

/**
 * Sets global context that will be included in all logs
 * @param {Object} context - Context object with requestId, userId, clientId, etc.
 * @returns {void}
 */
export function setContext(context) {
  globalContext = { ...globalContext, ...context };
}

/**
 * Clears the global context
 * @returns {void}
 */
export function clearContext() {
  globalContext = {};
}

/**
 * Logs an info-level message
 * @param {string} msg - Log message describing a standard event
 * @param {Object} [meta] - Optional structured metadata
 * @returns {void}
 * @example
 * logger.info('User logged in', { userId: 123, email: 'user@example.com' });
 */
export function info(msg, meta) {
  log('INFO', msg, meta);
}

/**
 * Logs a warning-level message
 * @param {string} msg - Log message describing a recoverable anomaly
 * @param {Object} [meta] - Optional structured metadata
 * @returns {void}
 * @example
 * logger.warn('API retry attempted', { endpoint: '/api/users', attempt: 2 });
 */
export function warn(msg, meta) {
  log('WARN', msg, meta);
}

/**
 * Logs an error-level message
 * @param {string} msg - Log message describing an unexpected failure
 * @param {Object|Error} [meta] - Error object or metadata
 * @returns {void}
 * @example
 * logger.error('Failed to fetch data', error);
 * logger.error('Validation failed', { field: 'email', value: 'invalid' });
 */
export function error(msg, meta) {
  // Handle Error objects specially
  if (meta instanceof Error) {
    log('ERROR', msg, {
      errorName: meta.name,
      errorMessage: meta.message,
      errorStack: meta.stack,
      ...meta
    });
  } else {
    log('ERROR', msg, meta);
  }
}

/**
 * Logs a debug-level message (dev mode only)
 * @param {string} msg - Verbose debug message
 * @param {Object} [meta] - Optional debug data
 * @returns {void}
 * @example
 * logger.debug('State updated', { oldState, newState });
 */
export function debug(msg, meta) {
  log('DEBUG', msg, meta);
}

/**
 * Logs raw debug data without redaction (dev mode only)
 * Only available in development environment
 * @param {*} data - Raw data to log
 * @returns {void}
 * @example
 * logger.debugRaw({ password: 'secret123', token: 'abc' }); // Only in dev
 */
export function debugRaw(data) {
  if (process.env.NODE_ENV === 'production') {
    console.warn('debugRaw is not available in production');
    return;
  }
  
  const logEntry = {
    level: 'DEBUG_RAW',
    data,
    ts: new Date().toISOString(),
    ...globalContext
  };
  
  console.log('[DEBUG_RAW]', logEntry);
}

/**
 * Creates a child logger with additional context
 * @param {Object} context - Additional context for this logger instance
 * @returns {Object} Logger instance with the additional context
 * @example
 * const requestLogger = logger.child({ requestId: 'abc-123' });
 * requestLogger.info('Processing request'); // Includes requestId automatically
 */
export function child(context) {
  const childContext = { ...globalContext, ...context };
  
  return {
    info: (msg, meta) => {
      log('INFO', msg, { ...childContext, ...meta });
    },
    warn: (msg, meta) => {
      log('WARN', msg, { ...childContext, ...meta });
    },
    error: (msg, meta) => {
      const errorMeta = meta instanceof Error ? {
        errorName: meta.name,
        errorMessage: meta.message,
        errorStack: meta.stack,
        ...meta
      } : meta;
      log('ERROR', msg, { ...childContext, ...errorMeta });
    },
    debug: (msg, meta) => {
      log('DEBUG', msg, { ...childContext, ...meta });
    },
    debugRaw: debugRaw,
    child: (additionalContext) => child({ ...childContext, ...additionalContext })
  };
}

/**
 * Sets the minimum log level
 * @param {string} level - Log level name (DEBUG, INFO, WARN, ERROR)
 * @returns {void}
 * @example
 * logger.setLevel('WARN'); // Only WARN and ERROR will be logged
 */
export function setLevel(level) {
  if (LOG_LEVELS[level] !== undefined) {
    currentLogLevel = LOG_LEVELS[level];
  } else {
    console.warn(`Invalid log level: ${level}. Valid levels: ${Object.keys(LOG_LEVELS).join(', ')}`);
  }
}

/**
 * Gets the current log level
 * @returns {string} Current log level name
 */
export function getLevel() {
  return Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === currentLogLevel) || 'INFO';
}

/**
 * Enables or disables redaction (dev mode only)
 * @param {boolean} enabled - Whether redaction should be enabled
 * @returns {void}
 */
export function setRedaction(enabled) {
  if (process.env.NODE_ENV === 'production') {
    console.warn('Cannot disable redaction in production');
    return;
  }
  redactionEnabled = enabled;
}

// Create the default logger instance
const logger = {
  info,
  warn,
  error,
  debug,
  debugRaw,
  setContext,
  clearContext,
  child,
  setLevel,
  getLevel,
  setRedaction
};

// Export as default and named exports for flexibility
export default logger;